/**
 * Vind een lege positie op het canvas voor een nieuw concept dat via de
 * Tabel-insert wordt aangemaakt. Heuristiek (per user-keuze 2026-05-23):
 *
 *  1. Probeer **rechts** van de bestaande nodes: x = maxRight + 60.
 *     Scan y in banden van 120 px door de verticale spread van de bestaande
 *     nodes plus 200 px overshoot beneden. Pak de eerste y waar geen
 *     bestaand vakje overlapt met de hypothetische nieuwe rect.
 *  2. Lukt rechts niet (alle banden bezet door brede nodes / zelf-overlap
 *     met containers): probeer **links** met x = minLeft − 60 − newWidth.
 *  3. Laatste fallback: rechts-boven van de bounding-box van alles, zonder
 *     overlap-check. Mag in theorie overlappen — de modelleur kan 'm
 *     daarna gewoon slepen. In de praktijk is dit pad zeldzaam.
 *
 * De helper kent geen viewport — die zit alleen in React Flow's
 * `useReactFlow().getViewport()` en GridPane staat buiten de
 * `<ReactFlowProvider>`-tree. Door alléén op concept-posities te varen
 * blijft de helper getter-vrij en deterministisch testbaar.
 */

export const NEW_NODE_DEFAULT_WIDTH = 140;
export const NEW_NODE_DEFAULT_HEIGHT = 60;

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const GAP = 60;
const BAND = 120;
const VERTICAL_OVERSHOOT = 200;

export function findEmptyPlacement(
  existing: Iterable<Rect>,
  newWidth = NEW_NODE_DEFAULT_WIDTH,
  newHeight = NEW_NODE_DEFAULT_HEIGHT,
  /**
   * Optionele anker-rect (typisch de canvas-positie van de cursor-rij in
   * de Tabel). Wanneer aanwezig zoekt de helper EERST een lege plek in de
   * directe omgeving (rechts → onder → links → boven) van het anker. Pas
   * als die vier kandidaten allemaal overlappen, valt 'ie terug op de
   * "rechts van álle nodes"-heuristiek. Resultaat: insert-via-tabel met
   * cursor op een rij plaatst het nieuwe vakje vlak naast die rij's
   * vakje op canvas, niet ergens buiten beeld rechts.
   */
  anchor?: Rect,
): { x: number; y: number } {
  const rects = [...existing].filter((r) => Number.isFinite(r.x) && Number.isFinite(r.y));
  if (rects.length === 0) {
    if (anchor) return { x: anchor.x + anchor.w + GAP, y: anchor.y };
    return { x: 200, y: 200 };
  }

  let minLeft = Number.POSITIVE_INFINITY;
  let maxRight = Number.NEGATIVE_INFINITY;
  let minTop = Number.POSITIVE_INFINITY;
  let maxBottom = Number.NEGATIVE_INFINITY;
  for (const r of rects) {
    if (r.x < minLeft) minLeft = r.x;
    if (r.x + r.w > maxRight) maxRight = r.x + r.w;
    if (r.y < minTop) minTop = r.y;
    if (r.y + r.h > maxBottom) maxBottom = r.y + r.h;
  }

  const overlaps = (cand: Rect): boolean => {
    for (const r of rects) {
      if (cand.x + cand.w + GAP / 2 < r.x) continue;
      if (cand.x > r.x + r.w + GAP / 2) continue;
      if (cand.y + cand.h + GAP / 2 < r.y) continue;
      if (cand.y > r.y + r.h + GAP / 2) continue;
      return true;
    }
    return false;
  };

  // Anker-fase: probeer 4 kardinale richtingen vlak naast het anker.
  if (anchor) {
    const around: Rect[] = [
      { x: anchor.x + anchor.w + GAP, y: anchor.y, w: newWidth, h: newHeight }, // rechts
      { x: anchor.x, y: anchor.y + anchor.h + GAP, w: newWidth, h: newHeight }, // onder
      { x: anchor.x - newWidth - GAP, y: anchor.y, w: newWidth, h: newHeight }, // links
      { x: anchor.x, y: anchor.y - newHeight - GAP, w: newWidth, h: newHeight }, // boven
    ];
    for (const cand of around) {
      if (!overlaps(cand)) return { x: cand.x, y: cand.y };
    }
    // Niet alle vier zijn vrij — val terug op de globale heuristiek.
  }

  const rightX = maxRight + GAP;
  const scanFromY = minTop;
  const scanToY = maxBottom + VERTICAL_OVERSHOOT;
  for (let y = scanFromY; y <= scanToY; y += BAND) {
    const cand: Rect = { x: rightX, y, w: newWidth, h: newHeight };
    if (!overlaps(cand)) return { x: cand.x, y: cand.y };
  }

  const leftX = minLeft - GAP - newWidth;
  for (let y = scanFromY; y <= scanToY; y += BAND) {
    const cand: Rect = { x: leftX, y, w: newWidth, h: newHeight };
    if (!overlaps(cand)) return { x: cand.x, y: cand.y };
  }

  return { x: rightX, y: minTop };
}
